import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import api from '../services/api';
import { getUser } from '../services/auth';

const HISTORY_READ_KEY  = 'fmz_history_read';
const INBOX_READ_KEY    = 'fmz_inbox_read';
const HISTORY_CLICKED_KEY = 'fmz_history_clicked';

function getReadSet(key) {
  try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')); }
  catch { return new Set(); }
}
function saveReadSet(key, set) {
  localStorage.setItem(key, JSON.stringify([...set]));
}

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [history,  setHistory]  = useState([]);
  const [jobs,     setJobs]     = useState([]);
  const [warranty, setWarranty] = useState([]);
  const [chatUnread, setChatUnread] = useState(0);
  const [onlineStaff, setOnlineStaff] = useState([]);

  const [historyRead,    setHistoryRead]    = useState(() => getReadSet(HISTORY_READ_KEY));
  const [inboxRead,      setInboxRead]      = useState(() => getReadSet(INBOX_READ_KEY));
  const [historyClicked, setHistoryClicked] = useState(() => getReadSet(HISTORY_CLICKED_KEY));

  const wsRef = useRef(null);
  const subscribersRef = useRef(new Set());

  // ── initial load ──────────────────────────────────────────────────────────
  const loadNotifications = useCallback(() => {
    api.get('/notifications').then(r => {
      setHistory(r.data.history  || []);
      setJobs(r.data.jobs        || []);
      setWarranty(r.data.warranty || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  // ── WebSocket with polling fallback ──────────────────────────────────────
  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host  = window.location.host;
    let reconnectTimer = null;
    let pollTimer = null;
    let heartbeatTimer = null;
    let wsConnected = false; // eslint-disable-line no-unused-vars

    function startPolling() {
      if (pollTimer) return;
      pollTimer = setInterval(() => {
        api.get('/notifications').then(r => {
          setHistory(r.data.history  || []);
          setJobs(r.data.jobs        || []);
          setWarranty(r.data.warranty || []);
        }).catch(() => {});
      }, 15000); // poll every 15s when WS unavailable
    }

    function stopPolling() {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    }

    function handleMessage(msg) {
      subscribersRef.current.forEach(fn => { try { fn(msg); } catch {} });
      if (msg.type === 'staff:online') {
        setOnlineStaff(msg.staff || []);
      }
      if (msg.type === 'chat_message') {
        setChatUnread(prev => prev + 1);
      }
      if (msg.type === 'job:created') {
        setJobs(prev => [msg.job, ...prev].slice(0, 5));
      }
      if (msg.type === 'job:updated') {
        setJobs(prev => {
          const filtered = prev.filter(j => j.jobcode !== msg.job.jobcode);
          return [msg.job, ...filtered].slice(0, 5);
        });
        if (msg.history) setHistory(prev => [msg.history, ...prev].slice(0, 10));
      }
      if (msg.type === 'warranty:created') {
        setWarranty(prev => [msg.warranty, ...prev].slice(0, 5));
      }
      if (msg.type === 'warranty:updated') {
        setWarranty(prev => {
          const filtered = prev.filter(w => w.jobcode !== msg.warranty.jobcode);
          return [msg.warranty, ...filtered].slice(0, 5);
        });
        if (msg.history) setHistory(prev => [msg.history, ...prev].slice(0, 10));
      }
    }

    function connect() {
      try {
        const socket = new WebSocket(`${proto}://${host}/ws`);
        wsRef.current = socket;

        socket.onopen = () => {
          wsConnected = true;
          stopPolling();
          const user = getUser();
          if (user) {
            socket.send(JSON.stringify({ type: 'staff:auth', username: user.username || user.nama }));
          }
          // Send heartbeat every 60s to keep lastSeen fresh
          if (heartbeatTimer) clearInterval(heartbeatTimer);
          heartbeatTimer = setInterval(() => {
            if (socket.readyState === 1) socket.send(JSON.stringify({ type: 'heartbeat' }));
          }, 60 * 1000);
        };

        socket.onmessage = (e) => {
          try { handleMessage(JSON.parse(e.data)); } catch {}
        };

        socket.onclose = () => {
          wsConnected = false;
          if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
          startPolling();
          reconnectTimer = setTimeout(connect, 10000);
        };

        socket.onerror = () => { socket.close(); };
      } catch {
        startPolling();
        reconnectTimer = setTimeout(connect, 10000);
      }
    }

    connect();
    return () => {
      clearTimeout(reconnectTimer);
      stopPolling();
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  // ── unread counts ─────────────────────────────────────────────────────────
  const historyUnread = history.filter(h => !historyRead.has(String(h.id))).length;
  const inboxIds      = [
    ...jobs.map(j => `j-${j.id || j.jobcode}`),
    ...warranty.map(w => `w-${w.id || w.jobcode}`),
  ];
  const inboxUnread = inboxIds.filter(id => !inboxRead.has(id)).length;

  // ── mark as read ──────────────────────────────────────────────────────────
  function markHistoryRead() {
    const next = new Set(history.map(h => String(h.id)));
    saveReadSet(HISTORY_READ_KEY, next);
    setHistoryRead(next);
  }

  function markHistoryItemClicked(id) {
    const next = new Set(historyClicked);
    next.add(String(id));
    saveReadSet(HISTORY_CLICKED_KEY, next);
    setHistoryClicked(next);
  }

  function markInboxRead() {
    const next = new Set(inboxIds);
    saveReadSet(INBOX_READ_KEY, next);
    setInboxRead(next);
  }

  function clearChatUnread() { setChatUnread(0); }

  return (
    <NotificationContext.Provider value={{
      history, jobs, warranty,
      historyUnread, inboxUnread,
      historyClicked,
      markHistoryRead, markInboxRead, markHistoryItemClicked,
      chatUnread, clearChatUnread,
      onlineStaff,
      subscribe: (fn) => {
        subscribersRef.current.add(fn);
        return () => subscribersRef.current.delete(fn);
      },
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
